/**
 * KpiCard — a headline metric: big value, optional delta chip and trend
 * sparkline. `delta`, `trend`, `sub` and `spark` are all optional, so a card
 * can be as plain as a label + value.
 */

import type { ReactNode } from 'react';
import { cx } from './cx';
import Sparkline from './Sparkline';

interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** Change indicator, e.g. '+12.4%'. */
  delta?: string;
  /** Arrow direction for the delta chip. */
  trend?: 'up' | 'down';
  /** Style the delta as a bad outcome (red) regardless of arrow direction. */
  deltaBad?: boolean;
  /** Supporting line under the value. */
  sub?: ReactNode;
  /** Trend series for the sparkline. */
  spark?: number[];
}

export default function KpiCard({
  label,
  value,
  delta,
  trend,
  deltaBad,
  sub,
  spark,
}: KpiCardProps) {
  const deltaCls = deltaBad ? 'text-red-700 bg-red-50' : 'text-emerald-700 bg-emerald-50';
  return (
    <div className="bg-surface border border-outline-variant rounded-xl shadow-card p-5 flex flex-col gap-4 hover:border-secondary/30 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs font-medium text-outline uppercase tracking-wider">{label}</div>
          <div className="mt-2 text-[26px] font-semibold font-headline text-on-surface leading-none tracking-tight">
            {value}
          </div>
        </div>
        {delta && (
          <span
            className={cx(
              'inline-flex items-center gap-0.5 px-2 py-1 rounded-md text-[11px] font-semibold shrink-0',
              deltaCls,
            )}
          >
            <span className="material-symbols-outlined text-[13px]">
              {trend === 'down' ? 'arrow_downward' : 'arrow_upward'}
            </span>
            {delta}
          </span>
        )}
      </div>
      {(sub || spark) && (
        <div className="flex items-end justify-between gap-3">
          {sub && <div className="text-xs text-outline">{sub}</div>}
          {spark && (
            <div className="ml-auto">
              <Sparkline data={spark} color={deltaBad ? '#F07D00' : '#000080'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
